import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { requestMagicLink } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useMutation } from "../lib/useApi";
import { Alert, Spinner } from "../components/States";

interface LocationState {
  from?: string;
}

/** Magic-link sign-in: enter email → POST /auth/request → "check your email".
 *  The link itself is emailed; we never see or display the token. STAGING: the
 *  worker has no email pipeline and returns `dev_token`, so we hop straight to
 *  /verify with it — the production path is unchanged (no token in response). */
export function SignInPage() {
  const { status } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const from = (location.state as LocationState | null)?.from ?? "/tickets";

  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const { run, pending, error } = useMutation(requestMagicLink);

  // Already signed in → skip straight to where they were headed.
  if (status === "authenticated") return <Navigate to={from} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    const res = await run(value);
    if (!res?.ok) return;
    if (res.dev_token) {
      // Staging-only short-circuit (see RequestLinkResponse.dev_token).
      navigate(`/verify?token=${encodeURIComponent(res.dev_token)}`, { state: { from } });
      return;
    }
    setSent(true);
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        {sent ? (
          <>
            <h1>Check your email</h1>
            <p className="lead">
              If <strong>{email.trim()}</strong> matches an account, we’ve sent a secure sign-in
              link. Open it on this device to continue. The link expires shortly and can be used
              once.
            </p>
            <Alert kind="info">Didn’t get it? Check spam, or request a new link below.</Alert>
            <button
              className="btn"
              style={{ width: "100%" }}
              onClick={() => {
                setSent(false);
              }}
            >
              Use a different email
            </button>
            <p className="hint" style={{ marginTop: 16 }}>
              You can still <Link to="/kb">browse the knowledge base</Link> without signing in.
            </p>
          </>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            <h1>Sign in</h1>
            <p className="lead">
              Enter your email and we’ll send you a secure sign-in link — no password needed.
            </p>

            {error ? <Alert kind="error">{error}</Alert> : null}

            <label className="field">
              <span className="label">Email address</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                inputMode="email"
                autoFocus
                placeholder="you@example.com"
                required
              />
            </label>

            <button
              className="btn primary"
              type="submit"
              disabled={pending || !email.trim()}
              style={{ width: "100%" }}
            >
              {pending ? <Spinner label="Sending…" /> : "Email me a sign-in link"}
            </button>

            <p className="hint" style={{ marginTop: 16 }}>
              Prefer to look first? <Link to="/kb">Browse the knowledge base</Link>.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
